import { useState } from 'react';
import { useSwrInfiniteApi } from '@hooks/useSwrApi';

interface UseSwrJobApplicantsProps {
  locale?: string;
  jobSlug: string;
  startIndex?: number;
  limit?: number;
  status: string;
  serverSideData?: ApiSchema<'ResumeResult'> | ApiSchema<'ErrorResponse'>;
  urlParams?: string;
  initialFilter?: string | null;
  successCallback?: (data?: ApiSchema<'ResumeResult'>[]) => void;
  shouldFetch?: boolean;
}

/**
 * @description SWR: fetch job applicants with infinite pagination.
 *
 * * **locale**: string. What locale to use for the request.
 * * **serverSideData**: ApiTalentPageV3Ok, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **startIndex**: number, optional. The first page number to fetch (i.e.: fetch starting with page 5). Default 1.
 * * **limit**: number, optional. The number of CVs to get per call. Modifies the *limit* url parameter (&limit=24). Default 24.
 * * **status**: string, optional. The status of the applicants to fetch. Default 'rejected'.
 * * **serverSideData**: ApiSchema<'ResumeResult'>, optional. The data we got from the server side request (if we do it). Default undefined.
 * * **urlParams**: '&sort=relevant', optional. Default empty string.
 * * **initialFilter**: string | null, optional. The initial filter to apply to the data. Default null.
 * * **successCallback**: function, optional. A function to call when the data is successfully fetched. Default undefined.
 * * **shouldFetch**: boolean, optional. If true, the data will be fetched. Default true.
 */
export const useSwrBoardJobApplicants = (props: UseSwrJobApplicantsProps) => {
  const {
    locale,
    jobSlug,
    startIndex = 1,
    limit = 24,
    status = 'rejected',
    urlParams = '',
    serverSideData,
    initialFilter = '',
    successCallback,
    shouldFetch = true,
  } = props;

  // State for filter
  const [extraColumnFilter, setExtraColumnFilter] = useState(initialFilter);

  // SWR Fetch Hook
  const {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage, // Reset pagination
    mutateSiblings,
    isEmpty,
    showLoadMore,
    sizeAsRef,
  } = useSwrInfiniteApi<ApiSchema<'ResumeResult'>>({
    url: `/v1/${jobSlug}/applicant-board/${status}`,
    locale,
    urlParams: `${urlParams}${extraColumnFilter ? `&${extraColumnFilter}` : ''}`, // Avoid adding empty filter=
    serverSideData,
    shouldFetch,
    infiniteConfig: {
      paginationType: 'page',
      limit,
      responseArrayKey: 'items',
      startIndex,
    },
    toastsOptions: {
      showErrorToast: true,
    },
    apiOptions: {
      keepPreviousData: true,
      revalidateOnFocus: false,
      revalidateIfStale: true,
      parallel: true,
      onSuccess: (newData) => {
        if (successCallback) {
          successCallback(newData);
        }
      },
    },
  });

  // Update filter and reset page to 1
  const updateFilter = (newFilter: string) => {
    setExtraColumnFilter(newFilter);
  };


  const lastItem = data?.length ? data.at(-1) : null;
  const showMoreData = !!showLoadMore || (!!lastItem && (lastItem.page ?? -1) < (lastItem.pages ?? 0));

  return {
    data,
    error,
    size,
    setSize,
    isValidating,
    isLoading,
    mutate,
    mutatePage,
    mutateSiblings,
    isEmpty,
    showLoadMore: showMoreData,
    sizeAsRef,
    extraColumnFilter,
    updateFilter,
  };
};
